import Layout from '@components/layout'
import { customPageModuleQuery } from '@data/sanity/queries/page'
import { SanityCustomPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { parseOptionalParameters } from '@lib/helpers'
import { Locale, getAllLocales } from '@lib/language'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext, StaticPathWithSlugs } from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getPage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getWebPageSchema } from '@lib/schema/web'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPathsResult, GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type PageProps = BasePageProps & {
  slugs: string[]
  page: SanityCustomPage
  site: SanitySiteFragment
}

const Page = ({ draftMode, locale, slugs, page, site }: PageProps) => {
  const schemas = useMemo(() => {
    const webPageSchema = getWebPageSchema(page, site)
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [webPageSchema, ...videoObjectSchemas]
  }, [page, site])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={customPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            pageSlugs: slugs,
          }}
        />
      )}
    </Layout>
  )
}

interface PageParams extends ParsedUrlQuery {
  slugs: string[]
}

export async function getStaticProps(
  context: CustomStaticPropsContext<PageParams>,
): Promise<GetStaticPropsResult<PageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slugs = parseOptionalParameters(context.params?.slugs) ?? []
  const page = await getPage(sanityClient, context.locale, slugs.join('/'))

  if (!page.page || page.page.type === 'template') {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      slugs,
      page: getNonNullValues(page.page),
      site: getNonNullValues(page.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<PageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allPages = await getAllSlugs(sanityClient, 'page')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlugs[] = allPages
    .map((page) => ({
      ...page,
      slug: page.slug.current,
      locale: page.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slugs: slug.split('/').filter((slug) => !!slug),
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default Page
